import { Subject, Subscription, filter, of, switchMap, tap, timer } from 'rxjs';

import type { CopilotPageNotification } from './copilot-notifications.service';

/** Which moments run a page's background check: a pause in typing, the author leaving the field, or both. */
export type CopilotBackgroundCheckTrigger = 'pause' | 'blur' | 'both';

export interface CopilotBackgroundCheckResult {
  /** False when the check could not run (the model failed): the same content is tried again. */
  checked: boolean;
  notification: {
    /** The stored notification's id; null when nothing is stored. */
    id: string | null;
    raisedAt: string;
    payload: unknown;
  } | null;
}

/**
 * A check a page asks the copilot layer to run on its content as the author
 * works — the page says what to check and how; the copilot layer decides
 * when, and delivers what it finds as a notification.
 */
export interface CopilotBackgroundCheck<T = unknown> {
  notificationType: string;
  trigger: CopilotBackgroundCheckTrigger;
  /** How long typing must pause before a check runs. */
  idleMs: number;
  /**
   * What to check now, or null when there is nothing to check. Content with
   * the same `key` is the same content: it is checked at most once a visit.
   */
  snapshot(): { key: string; value: T } | null;
  /** Runs the check. `visit` is what this page raised this visit, newest first, so a check can avoid repeating itself. */
  check(value: T, visit: CopilotPageNotification[]): Promise<CopilotBackgroundCheckResult>;
}

/** A finished check's notification, ready for the notification bus. */
export interface CopilotRaisedNotification {
  id: string;
  type: string;
  raisedAt: string;
  payload: unknown;
  stored: boolean;
}

/** The FNV-1a fingerprint the KB editor uses for its revisions: cheap, and enough to tell contents apart. */
function fingerprint(text: string): string {
  let hash = 2166136261;

  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  return `${text.length}:${(hash >>> 0).toString(36)}`;
}

/**
 * Runs one page's background check as the author works.
 *
 * - A change schedules a check for the next pause. Moments the trigger ignores
 *   are dropped before they can disturb anything.
 * - One check runs at a time. A moment that arrives while one is running
 *   waits; when it finishes, the newest content is checked — unless the author
 *   changed it again, in which case the next pause decides.
 * - Every answer reaches the page when it arrives, even if the author has
 *   moved on: what still applies is for the page to judge against what is on
 *   screen, and an answer dropped here would be a finding the author never
 *   gets.
 * - Content is checked at most once per visit. The running check is tracked
 *   by its content, so going back to it waits for it instead of paying twice;
 *   content that was answered is never sent again. A check that failed, or
 *   that could not complete, is tried again at the next moment.
 * - While paused (Tailor is open), no check starts and a finished check's
 *   notification is held. Resuming delivers what was held and, if the content
 *   changed meanwhile, checks it once.
 */
export class CopilotBackgroundCheckRunner<T = unknown> {
  private readonly events = new Subject<'changed' | 'settled' | 'free'>();
  private readonly subscription: Subscription;
  /** The one check in flight, by the fingerprint of the content it checks. */
  private running: { fingerprint: string } | null = null;
  /** A moment arrived while a check was running: check the newest content when it is done. */
  private checkWhenFree = false;
  /** Fingerprints of all content answered this visit. */
  private readonly checked = new Set<string>();
  private paused = false;
  /** A moment arrived while paused: check on resume. */
  private dueOnResume = false;
  /** Notifications that arrived while paused. */
  private held: CopilotRaisedNotification[] = [];
  private destroyed = false;

  constructor(
    private readonly source: CopilotBackgroundCheck<T>,
    private readonly onRaised: (notification: CopilotRaisedNotification) => void,
    private readonly visit: () => CopilotPageNotification[] = () => [],
  ) {
    const onPause = source.trigger !== 'blur';
    const onSettle = source.trigger !== 'pause';

    this.subscription = this.events
      .pipe(
        filter((event) => (event === 'changed' ? onPause : event === 'settled' ? onSettle : true)),
        switchMap((event) => (event === 'changed' ? timer(source.idleMs).pipe(tap(() => this.run())) : of(this.run()))),
      )
      .subscribe();
  }

  /** The content changed. */
  changed() {
    // Still changing: whatever was waiting for the running check is out of date.
    this.checkWhenFree = false;
    this.events.next('changed');
  }

  /** The author left the field. */
  settled() {
    this.events.next('settled');
  }

  setPaused(paused: boolean) {
    if (this.paused === paused || this.destroyed) {
      return;
    }

    this.paused = paused;

    if (paused) {
      return;
    }

    const held = this.held;

    this.held = [];
    held.forEach((notification) => this.onRaised(notification));

    if (this.dueOnResume) {
      this.dueOnResume = false;
      this.run();
    }
  }

  destroy() {
    this.destroyed = true;
    this.held = [];
    this.subscription.unsubscribe();
    this.events.complete();
  }

  private run() {
    if (this.paused) {
      this.dueOnResume = true;

      return;
    }

    const snapshot = this.source.snapshot();

    if (!snapshot) {
      return;
    }

    const fingerprinted = fingerprint(snapshot.key);

    if (this.checked.has(fingerprinted) || this.running?.fingerprint === fingerprinted) {
      return;
    }

    if (this.running) {
      this.checkWhenFree = true;

      return;
    }

    this.send(fingerprinted, snapshot.value);
  }

  /** Sends a check and tracks it until it settles, whoever is still listening. */
  private send(fingerprinted: string, value: T) {
    const running = { fingerprint: fingerprinted };
    const settle = (result?: CopilotBackgroundCheckResult) => {
      if (this.running === running) {
        this.running = null;
      }

      if (this.destroyed) {
        return;
      }

      // Only an answer counts as checked; a failure leaves the content to be tried again.
      if (result?.checked) {
        this.checked.add(fingerprinted);
      }

      if (result?.notification) {
        this.deliver({
          id: result.notification.id ?? crypto.randomUUID(),
          type: this.source.notificationType,
          raisedAt: result.notification.raisedAt,
          payload: result.notification.payload,
          stored: Boolean(result.notification.id),
        });
      }

      if (this.checkWhenFree) {
        this.checkWhenFree = false;
        this.events.next('free');
      }
    };

    this.running = running;
    this.source.check(value, this.visit()).then(settle, () => settle());
  }

  private deliver(notification: CopilotRaisedNotification) {
    if (this.paused) {
      this.held.push(notification);

      return;
    }

    this.onRaised(notification);
  }
}
